import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import QRCode from "qrcode";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import config from "../../config";
import axios from "axios";

function SectionCard({ title, subtitle, children }) {
  return (
    <Card>
      <CardContent>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h6" fontWeight={900} sx={{ color: "#fff" }}>
              {title}
            </Typography>
            {subtitle ? (
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)" }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </Stack>

        <Divider sx={{ my: 2 }} />
        {children}
      </CardContent>
    </Card>
  );
}

const textFieldSx = {
  "& .MuiInputLabel-root": {
    color: "#fff",
  },
  "& .MuiInputLabel-root.Mui-disabled": {
    color: "rgba(255,255,255,0.75)",
  },
  "& .MuiInputBase-input": {
    color: "#fff",
    WebkitTextFillColor: "#fff",
  },
  "& .MuiInputBase-input.Mui-disabled": {
    color: "rgba(255,255,255,0.85)",
    WebkitTextFillColor: "rgba(255,255,255,0.85)",
  },
  "& .MuiOutlinedInput-root": {
    color: "#fff",
    "& fieldset": {
      borderColor: "rgba(255,255,255,0.3)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(255,255,255,0.55)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#fff",
    },
    "&.Mui-error fieldset": {
      borderColor: "#f44336",
    },
    "&.Mui-disabled": {
      color: "rgba(255,255,255,0.85)",
      WebkitTextFillColor: "rgba(255,255,255,0.85)",
      backgroundColor: "rgba(255,255,255,0.04)",
    },
    "&.Mui-disabled fieldset": {
      borderColor: "rgba(255,255,255,0.18)",
    },
  },
  "& .MuiFormHelperText-root": {
    color: "rgba(255,255,255,0.72)",
  },
  "& .MuiFormHelperText-root.Mui-error": {
    color: "#ff8a80",
  },
};

function formatDate(date) {
  if (!date) return "-";
  return new Date(date).toLocaleString("el-GR");
}

function safeValue(value) {
  return value === null || value === undefined || value === "" ? "-" : value;
}

export default function ProfileAccountPage() {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState("");

  const [profileVisible, setProfileVisible] = useState(true);
  const [visibilityLoading, setVisibilityLoading] = useState(false);
  const [receiveEmails, setReceiveEmails] = useState(true);
  const [receiveEmailsLoading, setReceiveEmailsLoading] = useState(false);

  const [toast, setToast] = useState({
    open: false,
    type: "success",
    msg: "",
  });

  const notify = (type, msg) => {
    setToast({
      open: true,
      type,
      msg,
    });
  };

  const [fieldErrors, setFieldErrors] = useState({
    slug: "",
    personalPhone: "",
  });

  const [form, setForm] = useState({
    name: "",
    slug: "",
    personalPhone: "",
    personalEmail: "",
    vat: "",
    visible: false,
    subscribeUntil: "",
    created_at: "",
    accountStatus: "",
    stripeSubscriptionId: null,
    stripeCancelAtPeriodEnd: false,
    billingStatus: "none",
  });

  const [initialForm, setInitialForm] = useState({
    name: "",
    slug: "",
    personalPhone: "",
    personalEmail: "",
    vat: "",
    visible: false,
    subscribeUntil: "",
    created_at: "",
    accountStatus: "",
    stripeSubscriptionId: null,
    stripeCancelAtPeriodEnd: false,
    billingStatus: "none",
  });

  const dirty =
    form.slug !== initialForm.slug ||
    form.personalPhone !== initialForm.personalPhone;

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const token = useMemo(() => localStorage.getItem("admin_jwt"), []);

  const affiliateUrl = useMemo(() => {
    const savedSlug = initialForm.slug?.trim();
    if (!savedSlug) return "";
    return `${config.PUBLIC_FRONTEND_BASE_URL}/afiliate/${savedSlug}`;
  }, [initialForm.slug]);

  useEffect(() => {
    let isMounted = true;

    const generateQr = async () => {
      if (!affiliateUrl) {
        setQrDataUrl("");
        return;
      }

      try {
        setQrLoading(true);
        const dataUrl = await QRCode.toDataURL(affiliateUrl, {
          width: 320,
          margin: 2,
        });

        if (isMounted) {
          setQrDataUrl(dataUrl);
        }
      } catch (error) {
        if (isMounted) {
          setQrDataUrl("");
        }
      } finally {
        if (isMounted) {
          setQrLoading(false);
        }
      }
    };

    generateQr();

    return () => {
      isMounted = false;
    };
  }, [affiliateUrl]);

  const fetchMe = async () => {
    try {
      setLoading(true);
      setFieldErrors({ slug: "", personalPhone: "" });

      const res = await axios.get(`${config.BACKEND_BASE_URL}/api/admin/authentication/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = res.data?.data || {};

      const nextForm = {
        name: data.name || "",
        slug: data.slug || "",
        personalPhone: data.personalPhone || "",
        personalEmail: data.personalEmail || "",
        vat: data.vat || "",
        visible: Boolean(data.visible),
        subscribeUntil: data.subscribeUntil || "",
        created_at: data.created_at || "",
        accountStatus: data.accountStatus || "",
        stripeSubscriptionId: data.stripeSubscriptionId || null,
        stripeCancelAtPeriodEnd: Boolean(data.stripeCancelAtPeriodEnd),
        billingStatus: data.billingStatus || "none",
      };

      setForm(nextForm);
      setInitialForm(nextForm);
      setProfileVisible(Boolean(data.visible));
      setReceiveEmails(data.receiveEmails !== false);
    } catch (error) {
      notify(
        "error",
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message ||
          t("account.loadFailed")
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMe();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      setFieldErrors({ slug: "", personalPhone: "" });

      const res = await axios.patch(`${config.BACKEND_BASE_URL}/api/admin/authentication/me`, {
        slug: form.slug,
        personalPhone: form.personalPhone,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const updated = res.data?.data || {};

      const nextSlug = updated.slug ?? form.slug;
      const nextPersonalPhone = updated.personalPhone ?? form.personalPhone;

      setForm((prev) => ({
        ...prev,
        slug: nextSlug,
        personalPhone: nextPersonalPhone,
      }));

      setInitialForm((prev) => ({
        ...prev,
        slug: nextSlug,
        personalPhone: nextPersonalPhone,
      }));

      notify("success", res.data?.message || t("account.saveSuccess"));
    } catch (error) {
      const responseData = error?.response?.data;

      if (responseData?.errors && Array.isArray(responseData.errors)) {
        const nextFieldErrors = { slug: "", personalPhone: "" };

        responseData.errors.forEach((item) => {
          const field = item.path || item.param;
          if (field === "slug" || field === "personalPhone") {
            nextFieldErrors[field] = item.msg;
          }
        });

        setFieldErrors(nextFieldErrors);

        const firstErrorMsg =
          nextFieldErrors.slug ||
          nextFieldErrors.personalPhone ||
          responseData?.error ||
          responseData?.message ||
          t("account.saveFailed");

        notify("error", firstErrorMsg);
        return;
      }

      if (responseData?.fieldErrors) {
        const nextFieldErrors = {
          slug: responseData.fieldErrors.slug || "",
          personalPhone: responseData.fieldErrors.personalPhone || "",
        };

        setFieldErrors(nextFieldErrors);

        const firstErrorMsg =
          nextFieldErrors.slug ||
          nextFieldErrors.personalPhone ||
          responseData?.error ||
          responseData?.message ||
          t("account.saveFailed");

        notify("error", firstErrorMsg);
        return;
      }

      if (responseData?.error) {
        notify("error", responseData.error);
        return;
      }

      notify(
        "error",
        responseData?.message ||
          error?.message ||
          t("account.saveFailed")
      );
    } finally {
      setSaving(false);
    }
  };

  const handleProfileVisibilityChange = async (event) => {
    const checked = event.target.checked;
    const previous = profileVisible;
    setProfileVisible(checked);
    try {
      setVisibilityLoading(true);
      const res = await axios.patch(
        `${config.BACKEND_BASE_URL}/api/admin/dashboard/profile-visibility`,
        { visible: checked },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProfileVisible(res.data?.visible ?? checked);
      notify("success", t("dashboard.visibilitySuccess"));
    } catch (error) {
      setProfileVisible(previous);
      notify("error", error?.response?.data?.message || t("dashboard.visibilityError"));
    } finally {
      setVisibilityLoading(false);
    }
  };

  const handleReceiveEmailsChange = async (event) => {
    const checked = event.target.checked;
    const previous = receiveEmails;
    setReceiveEmails(checked);
    try {
      setReceiveEmailsLoading(true);
      const res = await axios.patch(
        `${config.BACKEND_BASE_URL}/api/admin/info/update/receive-emails`,
        { receiveEmails: checked },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReceiveEmails(res.data?.receiveEmails ?? checked);
      notify("success", t("account.emailNotifications.updateSuccess"));
    } catch (error) {
      setReceiveEmails(previous);
      notify("error", error?.response?.data?.message || t("account.emailNotifications.updateError"));
    } finally {
      setReceiveEmailsLoading(false);
    }
  };

  const handleCopyUrl = async () => {
    if (!affiliateUrl) return;

    try {
      await navigator.clipboard.writeText(affiliateUrl);
      notify("success", t("account.copySuccess"));
    } catch (error) {
      notify("error", t("account.copyFailed"));
    }
  };

  const handleDownloadQr = () => {
    if (!qrDataUrl || !affiliateUrl) return;

    try {
      const link = document.createElement("a");
      link.href = qrDataUrl;
      link.download = `${initialForm.slug || "affiliate"}-qr.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      notify("success", t("account.qrSuccess"));
    } catch (error) {
      notify("error", t("account.qrFailed"));
    }
  };

  return (
    <DashboardLayout>
      <Box py={3}>
        <Stack spacing={3}>
          <SectionCard
            title={t("account.sectionTitle")}
            subtitle={t("account.subtitle")}
          >
            {loading ? (
              <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress />
              </Box>
            ) : (
              <Stack spacing={3}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t("account.businessName")}
                      value={safeValue(form.name)}
                      disabled
                      fullWidth
                      sx={textFieldSx}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Slug"
                      value={form.slug}
                      onChange={(e) => setField("slug", e.target.value)}
                      fullWidth
                      error={Boolean(fieldErrors.slug)}
                      helperText={
                        fieldErrors.slug ||
                        t("account.slugHint")
                      }
                      sx={textFieldSx}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t("account.personalPhone")}
                      value={form.personalPhone}
                      onChange={(e) => setField("personalPhone", e.target.value)}
                      fullWidth
                      error={Boolean(fieldErrors.personalPhone)}
                      helperText={fieldErrors.personalPhone || " "}
                      sx={textFieldSx}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t("account.personalEmail")}
                      value={safeValue(form.personalEmail)}
                      disabled
                      fullWidth
                      sx={textFieldSx}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t("account.vat")}
                      value={safeValue(form.vat)}
                      disabled
                      fullWidth
                      sx={textFieldSx}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t("account.visibility")}
                      value={form.visible ? t("account.visible") : t("account.notVisible")}
                      disabled
                      fullWidth
                      sx={textFieldSx}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t("account.subscribeUntil")}
                      value={formatDate(form.subscribeUntil)}
                      disabled
                      fullWidth
                      sx={textFieldSx}
                    />
                    {form.subscribeUntil && (() => {
                      const until = new Date(form.subscribeUntil);
                      const now = new Date();
                      const twoWeeksFromNow = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);
                      const threeDaysAgo = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000);
                      const twoWeeksAgo = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000);
                      const willAutoRenew =
                        !!form.stripeSubscriptionId &&
                        !form.stripeCancelAtPeriodEnd &&
                        (form.billingStatus === "active" || form.billingStatus === "trialing");
                      if (until > twoWeeksFromNow || (until > now && willAutoRenew)) {
                        return (
                          <Typography variant="caption" sx={{ color: "success.main", mt: 0.5, display: "block" }}>
                            {t("account.subscriptionActive")}
                          </Typography>
                        );
                      }
                      if (until > now) {
                        return (
                          <Typography variant="caption" sx={{ color: "warning.main", mt: 0.5, display: "block" }}>
                            Η συνδρομή σας λήγει σε λιγότερο από 2 εβδομάδες
                          </Typography>
                        );
                      }
                      if (until > threeDaysAgo) {
                        return (
                          <Typography variant="caption" sx={{ color: "error.main", mt: 0.5, display: "block" }}>
                            Η συνδρομή σας μόλις έληξε
                          </Typography>
                        );
                      }
                      if (until > twoWeeksAgo) {
                        return (
                          <Typography variant="caption" sx={{ color: "error.main", mt: 0.5, display: "block" }}>
                            Η συνδρομή σας έχει λήξει
                          </Typography>
                        );
                      }
                      return (
                        <Typography variant="caption" sx={{ color: "warning.main", mt: 0.5, display: "block" }}>
                          Μπορείτε να αλλάξετε μόνο το slug
                        </Typography>
                      );
                    })()}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t("account.createdAt")}
                      value={formatDate(form.created_at)}
                      disabled
                      fullWidth
                      sx={textFieldSx}
                    />
                  </Grid>
                </Grid>

                <Alert
                  severity="info"
                  sx={{
                    bgcolor: "rgba(255,255,255,0.08)",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.25)",
                    "& .MuiAlert-icon": { color: "#fff" },
                  }}
                >
                  <span dangerouslySetInnerHTML={{ __html: t("account.editNote") }} />
                </Alert>

                <Box display="flex" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={loading || saving || !dirty}
                  >
                    {saving ? t("account.saving") : t("account.saveChanges")}
                  </Button>
                </Box>
              </Stack>
            )}

            <Snackbar
              open={toast.open}
              autoHideDuration={3000}
              onClose={() => setToast((prev) => ({ ...prev, open: false }))}
            >
              <Alert
                onClose={() => setToast((prev) => ({ ...prev, open: false }))}
                severity={toast.type}
                variant="filled"
              >
                {toast.msg}
              </Alert>
            </Snackbar>
          </SectionCard>

          <SectionCard
            title={t("account.share.sectionTitle")}
            subtitle={t("account.share.subtitle")}
          >
            {loading ? (
              <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={3} alignItems="stretch">
                <Grid item xs={12} md={7}>
                  <Stack spacing={2} height="100%">
                    <TextField
                      label="Affiliate URL"
                      value={affiliateUrl || "-"}
                      fullWidth
                      disabled
                      sx={textFieldSx}
                    />

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <Button
                        variant="contained"
                        onClick={handleCopyUrl}
                        disabled={!affiliateUrl}
                      >
                        {t("account.share.copyUrl")}
                      </Button>

                      <Button
                        variant="outlined"
                        onClick={handleDownloadQr}
                        disabled={!qrDataUrl || qrLoading}
                      >
                        {t("account.share.downloadQr")}
                      </Button>
                    </Stack>

                    <Alert
                      severity="info"
                      sx={{
                        bgcolor: "rgba(255,255,255,0.08)",
                        color: "#fff",
                        border: "1px solid rgba(255,255,255,0.25)",
                        "& .MuiAlert-icon": { color: "#fff" },
                      }}
                    >
                      {t("account.share.qrNote")}
                    </Alert>
                  </Stack>
                </Grid>

                <Grid item xs={12} md={5}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    minHeight="100%"
                    sx={{
                      border: "1px solid rgba(255,255,255,0.15)",
                      borderRadius: 2,
                      bgcolor: "rgba(255,255,255,0.03)",
                      p: 3,
                    }}
                  >
                    {qrLoading ? (
                      <CircularProgress />
                    ) : qrDataUrl ? (
                      <Box
                        component="img"
                        src={qrDataUrl}
                        alt="Affiliate QR Code"
                        sx={{
                          width: "100%",
                          maxWidth: 240,
                          height: "auto",
                          display: "block",
                          borderRadius: 1,
                          bgcolor: "#fff",
                          p: 1,
                        }}
                      />
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{ color: "rgba(255,255,255,0.75)", textAlign: "center" }}
                      >
                        {t("account.share.noSlug")}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            )}
          </SectionCard>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={900} sx={{ color: "#fff" }}>
                    {t("dashboard.profileVisibility")}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={profileVisible}
                        onChange={handleProfileVisibilityChange}
                        disabled={visibilityLoading || loading}
                      />
                    }
                    label={
                      <Typography sx={{ color: "#fff", fontSize: "0.95rem" }}>
                        {profileVisible ? t("dashboard.active") : t("dashboard.inactive")}
                      </Typography>
                    }
                    sx={{ m: 0 }}
                  />
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", display: "block", mt: 1 }}>
                    {t("dashboard.changeVisibility")}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={900} sx={{ color: "#fff" }}>
                    {t("account.emailNotifications.title")}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={receiveEmails}
                        onChange={handleReceiveEmailsChange}
                        disabled={receiveEmailsLoading || loading}
                      />
                    }
                    label={
                      <Typography sx={{ color: "#fff", fontSize: "0.95rem" }}>
                        {receiveEmails ? t("account.emailNotifications.active") : t("account.emailNotifications.inactive")}
                      </Typography>
                    }
                    sx={{ m: 0 }}
                  />
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", display: "block", mt: 1 }}>
                    {t("account.emailNotifications.hint")}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

        </Stack>
      </Box>
    </DashboardLayout>
  );
}
